import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Field } from './ui';

export type Adoption = {
  style: 'typed' | 'drawn';
  name: string;
  adopted: boolean;
  image_data_url?: string;
};

/**
 * Signature adoption with three equivalent routes (F07/F08):
 *   • Type — also the keyboard-only path; no pointer required.
 *   • Draw — pointer events cover mouse, pen and finger.
 * Adoption is always an explicit, separate confirmation. Nothing here infers
 * assent from arriving on the page.
 */
export function SignaturePad({
  defaultName, onChange, value,
}: {
  defaultName: string;
  value: Adoption | null;
  onChange: (adoption: Adoption | null) => void;
}) {
  const [mode, setMode] = useState<'typed' | 'drawn'>('typed');
  const [name, setName] = useState(value?.name || defaultName);
  const [adopted, setAdopted] = useState(Boolean(value?.adopted));
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const drawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const nameId = useId();
  const adoptId = useId();

  const sizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const next = { w: Math.floor(rect.width * ratio), h: Math.floor(rect.height * ratio) };
    if (canvas.width === next.w && canvas.height === next.h) return;
    canvas.width = next.w;
    canvas.height = next.h;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineWidth = 2.4;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#0b2452';
  }, []);

  useEffect(() => {
    sizeCanvas();
    const observer = new ResizeObserver(sizeCanvas);
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [sizeCanvas, mode]);

  const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const startStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    lastPoint.current = pointFrom(event);
    setAdopted(false);
  };

  const continueStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    event.preventDefault();
    const context = canvasRef.current?.getContext('2d');
    const point = pointFrom(event);
    if (!context || !lastPoint.current) return;
    context.beginPath();
    context.moveTo(lastPoint.current.x, lastPoint.current.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    lastPoint.current = point;
    setHasInk(true);
  };

  const endStroke = () => { drawing.current = false; lastPoint.current = null; };

  const clearInk = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    setAdopted(false);
  };

  /**
   * Draw the typed name exactly as the preview shows it.
   *
   * The server used to re-render a typed signature in Times-Italic while this
   * screen displayed a handwriting face, so the finished agreement showed the
   * signer's name in italics rather than the mark they adopted. Sending the
   * rendering removes the second opinion: what is stamped is what was shown.
   *
   * The font is read from the live computed style rather than hardcoded, so
   * whatever `--signature-font` actually resolved to on *this* device is what
   * gets captured -- including a fallback, which is still what the signer saw.
   */
  const renderTypedMark = useCallback((text: string): string | undefined => {
    const probe = previewRef.current;
    if (!probe) return undefined;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const style = window.getComputedStyle(probe);
    const family = style.fontFamily || 'cursive';
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return undefined;

    // Measure at a fixed size, then size the bitmap to the ink.
    const fontSize = 64;
    context.font = `${fontSize}px ${family}`;
    const metrics = context.measureText(text);
    const width = Math.ceil(metrics.width) + 24;
    const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8;
    const descent = metrics.actualBoundingBoxDescent || fontSize * 0.3;
    const height = Math.ceil(ascent + descent) + 20;

    canvas.width = Math.max(1, Math.floor(width * ratio));
    canvas.height = Math.max(1, Math.floor(height * ratio));
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    // Transparent background: the mark sits on whatever the page already is.
    context.font = `${fontSize}px ${family}`;
    context.fillStyle = '#0b2452';
    context.textBaseline = 'alphabetic';
    context.fillText(text, 12, ascent + 10);
    return canvas.toDataURL('image/png');
  }, []);

  // Publish the adoption upward only while it is actually complete.
  useEffect(() => {
    if (!adopted || name.trim().length < 2) { onChange(null); return; }
    if (mode === 'drawn') {
      if (!hasInk) { onChange(null); return; }
      const data = canvasRef.current?.toDataURL('image/png');
      onChange({ style: 'drawn', name: name.trim(), adopted: true, image_data_url: data });
    } else {
      // Still `typed` -- the style is what the signer chose and what the
      // evidence records. The image is only how it looked.
      onChange({ style: 'typed', name: name.trim(), adopted: true,
                 image_data_url: renderTypedMark(name.trim()) });
    }
  }, [adopted, name, mode, hasInk, onChange, renderTypedMark]);

  return (
    <div className="stack">
      <div className="tabs" role="tablist" aria-label="Signature style">
        <button type="button" role="tab" className="tab" aria-selected={mode === 'typed'}
                onClick={() => { setMode('typed'); setAdopted(false); }}>
          Type it
        </button>
        <button type="button" role="tab" className="tab" aria-selected={mode === 'drawn'}
                onClick={() => { setMode('drawn'); setAdopted(false); }}>
          Draw it
        </button>
      </div>

      <Field label="Your full name" id={nameId}
             hint="This is the name recorded as having signed.">
        <input
          id={nameId}
          className="input"
          value={name}
          autoComplete="name"
          onChange={(event) => { setName(event.target.value); setAdopted(false); }}
        />
      </Field>

      {mode === 'typed' ? (
        <div>
          <span className="field__label">Preview</span>
          <div className="sig-preview" aria-live="polite" ref={previewRef}>
            {name.trim() || <span className="muted" style={{ fontFamily: 'var(--font)', fontSize: '.9rem' }}>
              Enter your name above
            </span>}
          </div>
          <span className="field__hint">
            Typing your name works with a keyboard alone — no drawing required.
          </span>
        </div>
      ) : (
        <div>
          <span className="field__label">Draw your signature</span>
          <div className="sigpad">
            <canvas
              ref={canvasRef}
              onPointerDown={startStroke}
              onPointerMove={continueStroke}
              onPointerUp={endStroke}
              onPointerLeave={endStroke}
              onPointerCancel={endStroke}
              aria-label="Signature drawing area"
            />
            <div className="sigpad__baseline" aria-hidden="true" />
            {!hasInk && <div className="sigpad__hint">Draw with your finger, pen or mouse</div>}
          </div>
          <div className="row row--end" style={{ marginTop: 8 }}>
            <button type="button" className="btn btn--small btn--ghost" onClick={clearInk}>
              Clear
            </button>
          </div>
        </div>
      )}

      <label className="checkbox" htmlFor={adoptId}>
        <input
          id={adoptId}
          type="checkbox"
          checked={adopted}
          onChange={(event) => setAdopted(event.target.checked)}
        />
        <span className="checkbox__text">
          I adopt this as my electronic signature and initials for this agreement.
        </span>
      </label>
    </div>
  );
}
